import { useId, useMemo, useState } from "react";
import useDealStore from "../../states/useDealStore";
import { updateDealStage } from "../../services/contact_service";
import DealDetailsModal from "../modals/deals/[id]";
import { Input, KanbanBoard as SharedKanbanBoard, Modal } from "@iq/models";
import useAuthStore from "../../../states/authStore";
import { DEAL_STAGES, normalizeDealStage } from "./dealStageModel";
import "./KanbanBoard.css";
import { LOST_REASONS } from "../../models/dealClosureModel";
import "../modals/Modal.css";

function LostDealModal({ move, onCancel, onConfirm }) {
  const formId = useId();
  const [lostReason, setLostReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (!lostReason) {
      setError("Select a lost reason.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onConfirm(move, lostReason);
    } catch (submitError) {
      setError(submitError?.message || "Failed to mark deal lost");
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={saving ? undefined : onCancel}
      title="Mark Deal Lost"
      actionButtons={[{
        key: "mark-lost",
        text: saving ? "Saving..." : "Mark Lost",
        type: "submit",
        form: formId,
        color: "danger",
        disabled: saving || !lostReason,
      }]}
      errorMessage={error}
      shrink={true}
    >
      <form id={formId} className="modal-form-container" onSubmit={handleSubmit}>
        <div className="modal-form-field wide-form-field">
          <label htmlFor="deal-lost-reason">Lost reason</label>
          <Input
            type="dropdown"
            id="deal-lost-reason"
            value={LOST_REASONS.find((reason) => reason.value === lostReason)?.label || ""}
            placeholder="Select a reason"
            allowCustom={false}
            colorScheme="light"
            options={LOST_REASONS}
            onOptionSelect={(option) => setLostReason(option.value)}
          />
        </div>
      </form>
    </Modal>
  );
}

function parseNumber(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = String(value).replace(/[$,\s]/g, "").trim();
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDealValueLabel(deal) {
  const cents = parseNumber(deal?.amount_cents);
  const wholeAmount = cents != null ? cents / 100 : null;
  const fallbackAmount =
    wholeAmount ??
    parseNumber(deal?.deal_value) ??
    parseNumber(deal?.value) ??
    parseNumber(deal?.amount);

  if (fallbackAmount == null) {
    return "";
  }

  const currency = String(deal?.currency || "USD").toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(fallbackAmount);
  } catch {
    return `$${fallbackAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
}

function resolveContactLabel(deal) {
  const contact = deal?.contact;

  if (typeof contact === "string") {
    return contact.trim();
  }

  if (contact && typeof contact === "object") {
    const firstName = String(contact?.first_name || "").trim();
    const lastName = String(contact?.last_name || "").trim();
    const fullName = `${firstName} ${lastName}`.trim();

    return (
      fullName ||
      String(contact?.name || "").trim() ||
      String(contact?.email || "").trim() ||
      ""
    );
  }

  return (
    String(deal?.contact_name || "").trim() ||
    String(deal?.manual_contact_name || "").trim() ||
    ""
  );
}

function resolveDealOwnerLabel(deal, profile) {
  const directLabel = String(
    deal?.owner_label ?? deal?.owner_name ?? deal?.ownerName ?? ""
  ).trim();
  if (directLabel) {
    return directLabel;
  }

  const ownerObject = deal?.owner_user ?? deal?.ownerUser ?? deal?.owner;
  if (ownerObject && typeof ownerObject === "object") {
    const objectLabel = String(
      ownerObject?.name ?? ownerObject?.full_name ?? ownerObject?.email ?? ""
    ).trim();
    if (objectLabel) {
      return objectLabel;
    }
  }

  const ownerUserId = deal?.owner_user_id ?? deal?.ownerUserId ?? deal?.owner_id ?? deal?.ownerId;
  if (ownerUserId == null || ownerUserId === "") {
    return "";
  }

  if (Number(ownerUserId) === Number(profile?.id)) {
    return String(profile?.name || "You").trim();
  }

  return `User #${ownerUserId}`;
}

function KanbanBoardAdapter() {
  const deals = useDealStore((state) => state.deals);
  const dealsLoading = useDealStore((state) => state.loading);
  const updateDealStageInState = useDealStore((state) => state.updateDealStageInState);
  const profile = useAuthStore((state) => state.profile);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [pendingLostMove, setPendingLostMove] = useState(null);

  const dealsForBoard = useMemo(
    () =>
      (Array.isArray(deals) ? deals : []).map((deal) => ({
        ...deal,
        owner_label: resolveDealOwnerLabel(deal, profile),
        boardCard: {
          title: String(deal?.title || "Untitled deal").trim() || "Untitled deal",
          ownerLabel: resolveDealOwnerLabel(deal, profile),
          accountLabel: String(deal?.account || "").trim(),
          contactLabel: resolveContactLabel(deal),
          valueLabel: formatDealValueLabel(deal),
        },
      })),
    [deals, profile]
  );

  async function persistMove(move, lostReason = "") {
    const dealId = Number(move.itemId);
    const updated = await updateDealStage(dealId, move.toColumnId, lostReason);
    updateDealStageInState(dealId, updated || { stage: move.toColumnId });
    setPendingLostMove(null);
  }

  async function handleMove(move) {
    if (!move || move.fromColumnId === move.toColumnId) {
      return;
    }

    const dealId = Number(move.itemId);
    if (!Number.isFinite(dealId)) {
      throw new Error("Invalid deal id in kanban move payload");
    }

    if (move.toColumnId === "lost") {
      setPendingLostMove(move);
      return;
    }

    await persistMove(move);
  }

  return (
    <>
      <SharedKanbanBoard
        boardType="crm"
        items={dealsForBoard}
        columns={DEAL_STAGES}
        getItemId={(deal) => deal?.id}
          getColumnIdForItem={(deal) =>
            normalizeDealStage(
              deal?.stage ||
              deal?.deal_stage ||
              deal?.dealStage ||
              deal?.status
            )
          }
        onMove={handleMove}
        onMoveError={(error, move) => {
          console.error("Failed to persist kanban move", { error, move });
        }}
        renderCard={({ item }) => {
          const boardCard = item?.boardCard || {};
          return (
            <>
              <div className="crm-kanban-card-header">
                <strong className="crm-kanban-card-title">{boardCard.title || "Untitled deal"}</strong>
                {boardCard.ownerLabel ? <div className="crm-kanban-card-owner">{boardCard.ownerLabel}</div> : null}
              </div>
              {boardCard.accountLabel ? (
                <div className="crm-kanban-card-meta">
                  <span className="crm-kanban-card-meta-label">Account:</span>{" "}
                  <span>{boardCard.accountLabel}</span>
                </div>
              ) : null}
              {boardCard.contactLabel ? (
                <div className="crm-kanban-card-meta">
                  <span className="crm-kanban-card-meta-label">Contact:</span>{" "}
                  <span>{boardCard.contactLabel}</span>
                </div>
              ) : null}
              {boardCard.valueLabel ? <div className="crm-kanban-card-value">{boardCard.valueLabel}</div> : null}
            </>
          );
        }}
        onCardClick={(item) => {
          const dealId = Number(item?.id);
          if (Number.isFinite(dealId)) {
            setSelectedDealId(dealId);
          }
        }}
        loading={dealsLoading}
        emptyState="No deals found."
      />

      {selectedDealId ? (
        <DealDetailsModal
          dealId={selectedDealId}
          onClose={() => setSelectedDealId(null)}
        />
      ) : null}
      {pendingLostMove ? (
        <LostDealModal
          move={pendingLostMove}
          onCancel={() => setPendingLostMove(null)}
          onConfirm={persistMove}
        />
      ) : null}
    </>
  );
}

export default KanbanBoardAdapter;
